import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  AddRounded,
  ArrowForwardRounded,
  CloudQueueRounded,
  CodeRounded,
  DataObjectRounded,
  DesignServicesRounded,
  InsightsRounded,
  PsychologyRounded,
  ScienceRounded,
  ViewKanbanRounded,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { isInterviewFreeAccessMode } from "../utils/accessMode";

const filters = ["All Tracks", "Technical", "Behavioral", "HR", "Role Specific"];

const tracks = [
  {
    title: "Software Engineer",
    description: "Technical interviews for software engineering roles.",
    questions: 45,
    difficulty: "Intermediate",
    duration: "35 min",
    category: "Technical",
    type: "technical",
    icon: CodeRounded,
    color: "#6d5dfc",
    tint: "#f1efff",
  },
  {
    title: "Data Scientist",
    description: "Machine learning, analytics, and data science interviews.",
    questions: 40,
    difficulty: "Advanced",
    duration: "40 min",
    category: "Role Specific",
    type: "technical",
    icon: ScienceRounded,
    color: "#17a96b",
    tint: "#eaf9f1",
  },
  {
    title: "Product Manager",
    description: "Product sense, prioritization, and product strategy.",
    questions: 35,
    difficulty: "Intermediate",
    duration: "30 min",
    category: "Behavioral",
    type: "mixed",
    icon: ViewKanbanRounded,
    color: "#f59e0b",
    tint: "#fff7e6",
  },
  {
    title: "Frontend Developer",
    description: "HTML, CSS, JavaScript, and frontend architecture.",
    questions: 35,
    difficulty: "Intermediate",
    duration: "30 min",
    category: "Technical",
    type: "technical",
    icon: DataObjectRounded,
    color: "#4f6bff",
    tint: "#edf1ff",
  },
  {
    title: "Backend Developer",
    description: "APIs, databases, distributed systems, and reliability.",
    questions: 45,
    difficulty: "Advanced",
    duration: "40 min",
    category: "Technical",
    type: "technical",
    icon: InsightsRounded,
    color: "#635bff",
    tint: "#efefff",
  },
  {
    title: "DevOps Engineer",
    description: "Cloud infrastructure, CI/CD, and operations interviews.",
    questions: 40,
    difficulty: "Advanced",
    duration: "40 min",
    category: "Role Specific",
    type: "technical",
    icon: CloudQueueRounded,
    color: "#1689e8",
    tint: "#eaf5ff",
  },
  {
    title: "UX Designer",
    description: "Design process, research, critique, and collaboration.",
    questions: 30,
    difficulty: "Intermediate",
    duration: "25 min",
    category: "Behavioral",
    type: "behavioral",
    icon: DesignServicesRounded,
    color: "#e94f9b",
    tint: "#fff0f7",
  },
  {
    title: "Business Analyst",
    description: "Requirements, stakeholder alignment, and business cases.",
    questions: 35,
    difficulty: "Intermediate",
    duration: "30 min",
    category: "HR",
    type: "mixed",
    icon: PsychologyRounded,
    color: "#0f9f8f",
    tint: "#e9f9f7",
  },
];

export default function InterviewsPage() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState(filters[0]);
  const freeAccessMode = isInterviewFreeAccessMode();
  const visibleTracks = activeFilter === filters[0]
    ? tracks
    : tracks.filter((track) => track.category === activeFilter);

  const startTrack = (track) => {
    navigate("/interview-config", { state: { type: track.type, role: track.title } });
  };

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "#fff", color: "#0b1020" }}>
      <Container maxWidth="lg" sx={{ px: { xs: 2.5, sm: 4, lg: 6 }, py: { xs: 4, md: 6 } }}>
        <Box component="header" sx={{ mb: 4 }}>
          <Button
            variant="text"
            onClick={() => navigate("/dashboard")}
            sx={{ px: 0, mb: 1, color: "#4b5563", minHeight: 44 }}
          >
            Back to Dashboard
          </Button>
          <Typography component="h1" sx={{ fontSize: { xs: 32, md: 40 }, lineHeight: 1.15, fontWeight: 800 }}>
            Interview Tracks
          </Typography>
          <Typography sx={{ mt: 1, color: "#6b7280", fontSize: { xs: 15, md: 16 } }}>
            {freeAccessMode
              ? "Choose a track and start practicing with an AI interview tailored to the role. Free access is enabled, so you can start without a trial code."
              : "Choose a track and start practicing with an AI interview tailored to the role."}
          </Typography>
        </Box>

        <Stack
          component="nav"
          aria-label="Interview track filters"
          direction="row"
          spacing={0.5}
          sx={{
            mb: 3,
            borderBottom: "1px solid #e8ecf4",
            overflowX: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {filters.map((filter) => {
            const active = activeFilter === filter;
            return (
              <Button
                key={filter}
                type="button"
                aria-pressed={active}
                onClick={() => setActiveFilter(filter)}
                sx={{
                  flex: "0 0 auto",
                  minHeight: 44,
                  px: 2,
                  borderRadius: 0,
                  color: active ? "#3e5af5" : "#6b7280",
                  borderBottom: "2px solid",
                  borderColor: active ? "#4f6bff" : "transparent",
                  fontWeight: active ? 700 : 600,
                  "&:hover": { bgcolor: "#f8faff" },
                  "&:focus-visible": { outline: "3px solid #cbd5ff", outlineOffset: -3 },
                }}
              >
                {filter}
              </Button>
            );
          })}
        </Stack>

        <Grid component="ul" container spacing={2.5} sx={{ p: 0, m: 0, listStyle: "none" }}>
          {visibleTracks.map((track) => {
            const TrackIcon = track.icon;
            return (
              <Grid component="li" item xs={12} sm={6} md={4} key={track.title}>
                <Paper
                  elevation={0}
                  sx={{
                    height: "100%",
                    p: 2.5,
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid #e8ecf4",
                    borderRadius: "12px",
                    boxShadow: "none",
                    transition: "transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease",
                    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                    "&:hover": {
                      transform: "translateY(-2px)",
                      borderColor: "#cbd5ff",
                      boxShadow: "0 12px 28px rgba(15, 23, 42, 0.07)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      display: "grid",
                      placeItems: "center",
                      borderRadius: "12px",
                      bgcolor: track.tint,
                      color: track.color,
                      mb: 2.25,
                    }}
                  >
                    <TrackIcon aria-hidden="true" sx={{ fontSize: 28 }} />
                  </Box>

                  <Typography component="h2" sx={{ fontWeight: 750, fontSize: 18, lineHeight: 1.3 }}>
                    {track.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.75, minHeight: { sm: 44 } }}>
                    {track.description}
                  </Typography>

                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
                    <Chip size="small" label={`${track.questions} Questions`} sx={{ bgcolor: "#f8fafc", color: "#4b5563" }} />
                    <Chip size="small" label={track.difficulty} sx={{ bgcolor: track.tint, color: track.color }} />
                    <Chip size="small" label={track.duration} sx={{ bgcolor: "#f8fafc", color: "#4b5563" }} />
                  </Stack>

                  <Button
                    variant="text"
                    endIcon={<ArrowForwardRounded />}
                    onClick={() => startTrack(track)}
                    aria-label={track.title === "Software Engineer" ? "Configure Technical Interview" : `Start ${track.title} track`}
                    sx={{ mt: 2, px: 0, alignSelf: "flex-start", color: "#3e5af5", minHeight: 44 }}
                  >
                    Start track
                  </Button>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        <Paper
          component="section"
          elevation={0}
          sx={{
            mt: 3,
            p: { xs: 2.5, sm: 3 },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
            bgcolor: "#f7f8ff",
            border: "1px solid #e2e5ff",
            borderRadius: "12px",
            boxShadow: "none",
          }}
        >
          <Box>
            <Typography component="h2" sx={{ fontWeight: 750, fontSize: 17 }}>
              Can't find your role?
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
              Request a custom track and we'll create it for you.
            </Typography>
          </Box>
          <Button
            component="a"
            href="mailto:support@evaluateyourself.ai?subject=Interview%20track%20request"
            variant="outlined"
            startIcon={<AddRounded />}
            sx={{ bgcolor: "#fff", borderColor: "#cbd5ff", color: "#3e5af5", minWidth: 156 }}
          >
            Request Track
          </Button>
        </Paper>
      </Container>
    </Box>
  );
}
